import {
  DateFragment,
  DateRegion,
  type GenericRange,
  type NaiveDate,
  type NaiveDateTime,
  type TimeOfDay,
  type TimezoneRegion,
} from "@tento-chrono";
import type { Option } from "@tento-lona/sheets";
import { CalendarColumn, PARTIAL_EDGE_RADIUS_PX } from "@tento-ui/components/calendar/calendar-column";
import { PIXELS_PER_HOUR } from "@tento-ui/components/calendar/calendar-hour";

/**
 * The calendar column's skeleton: the day background, hour lines and day
 * dividers a body column paints from the moment it is laid out, before any
 * day binds and whether or not its events have arrived.
 *
 * The skeleton reads the same custom properties the bound day reads
 * (`--std-calendar-column-layout-horizontal-color` and
 * `--std-calendar-column-layout-width` for the hour lines, the theme's
 * background for the day), so a day binding on top of it lands on identical
 * pixels. Its per-column inputs are a pure function of the column's date
 * range and window: day binds and data arrival never write them.
 */
export const SKELETON_PROPERTY_NAMES = [
  "--column-skeleton-bg",
  "--column-skeleton-days",
  "--column-skeleton-day-divider",
] as const;

export type SkeletonPropertyName = (typeof SKELETON_PROPERTY_NAMES)[number];

export interface ColumnSkeletonInput {
  /** Days the column spans: 7 for a week column, 1 for a day column. */
  readonly days: number;
  /** The day background, from the same theme callback a day bind uses. */
  readonly background: string;
  /** A windowed or divider-less column draws no inner day dividers. */
  readonly innerDividers: boolean;
}

export function columnSkeletonProperties(
  input: ColumnSkeletonInput,
): Record<SkeletonPropertyName, string> {
  return {
    "--column-skeleton-bg": input.background,
    "--column-skeleton-days": String(Math.max(1, input.days)),
    "--column-skeleton-day-divider": input.innerDividers ? "var(--divider)" : "transparent",
  };
}

/**
 * The span of a day column where events can render, as skeleton inputs. A
 * day with a flight (the column key's partial window: before the departure,
 * after the arrival, or between the two) renders events only inside it, and
 * the rest of the day paints the column background (`--column-bg`), as it
 * does around the bound day. Every other column has no outside.
 *
 * The span's top and bottom are the bound day's first and last edges, and
 * the skeleton rounds each at `PARTIAL_EDGE_RADIUS_PX` (`-radius`, else 0px)
 * exactly when `CalendarColumn.partialEdges` makes it a partial edge, so the
 * wedges outside the column's round corners show the outside, not a square of
 * day background, and no wedge cuts a square column edge.
 */
export const SKELETON_SPAN_PROPERTY_NAMES = [
  "--column-skeleton-outside-bg",
  "--column-skeleton-span-top",
  "--column-skeleton-span-bottom",
  "--column-skeleton-span-top-radius",
  "--column-skeleton-span-bottom-radius",
] as const;

export type SkeletonSpanPropertyName = (typeof SKELETON_SPAN_PROPERTY_NAMES)[number];

export interface ColumnSkeletonSpanInput {
  /** The column's first date (a windowed column spans exactly one day). */
  readonly date: NaiveDate;
  /** The hours the column shows; its height is `window.duration` hours. */
  readonly window: TimeOfDay.Range;
  /** The column key's partial window (`partialLocalTop` / `partialLocalBottom`). */
  readonly partialWindow: GenericRange<Option<NaiveDateTime>>;
  /** The column background painted outside the span. */
  readonly outside: string;
  readonly pixelsPerHour: number;
  /**
   * The column's zone: the day's zone fragments, whose first and last shown
   * edges are the span's. On a DST day a fragment edge inside the hours
   * window is a partial edge the column rounds (its seam); evaluated under
   * UTC the day has one fragment and the skeleton painted a square corner
   * there.
   */
  readonly zone: TimezoneRegion;
}

/**
 * The column's partial-edge decision (`CalendarColumn.partialEdges`) for the
 * span's two edges: the top of the day's first shown zone fragment and the
 * bottom of its last, windowed as the bound day windows them (a fragment the
 * window leaves no hours of hides, and has no edge).
 */
function spanPartialEdges(input: ColumnSkeletonSpanInput): CalendarColumn.PartialEdges {
  const shown: CalendarColumn.PartialEdges[] = [];
  for (const fragment of new DateRegion(input.date, input.zone).dateFragments()) {
    if (fragment == null) continue;
    const edges = CalendarColumn.partialEdges(new DateFragment.Windowed(fragment, input.partialWindow, input.window));
    if (edges != null) shown.push(edges);
  }
  return { top: shown[0]?.top ?? false, bottom: shown[shown.length - 1]?.bottom ?? false };
}

/**
 * The span is placed by time of day within `window`, the way
 * `DateFragment.Windowed` places the bound day (its `applyAll` and
 * `getPositioning`): it starts at the later of the window start and the
 * partial top, and ends at the earlier of the window end and the partial
 * bottom.
 */
export function columnSkeletonSpanProperties(
  input: ColumnSkeletonSpanInput,
): Record<SkeletonSpanPropertyName, string> {
  const { start, end } = input.partialWindow;
  const partial = spanPartialEdges(input);
  const radius = (edge: boolean) => (edge ? PARTIAL_EDGE_RADIUS_PX + "px" : "0px");
  if (start == null && end == null) {
    return {
      "--column-skeleton-outside-bg": partial.top || partial.bottom ? input.outside : "transparent",
      "--column-skeleton-span-top": "0px",
      "--column-skeleton-span-bottom": "100%",
      "--column-skeleton-span-top-radius": radius(partial.top),
      "--column-skeleton-span-bottom-radius": radius(partial.bottom),
    };
  }
  const hours = (ndt: NaiveDateTime) =>
    ndt.date.differenceInDays(input.date) * 24 + ndt.time.toMs / 3_600_000;
  const windowStart = input.window.start.toMs / 3_600_000;
  const windowEnd = windowStart + input.window.duration.toHrsF;
  const top = Math.min(windowEnd, Math.max(windowStart, start == null ? windowStart : hours(start)));
  const bottom = end == null ? null : Math.max(top, Math.min(windowEnd, hours(end)));
  return {
    "--column-skeleton-outside-bg": input.outside,
    "--column-skeleton-span-top": (top - windowStart) * input.pixelsPerHour + "px",
    "--column-skeleton-span-bottom": bottom == null ? "100%" : (bottom - windowStart) * input.pixelsPerHour + "px",
    "--column-skeleton-span-top-radius": radius(partial.top),
    "--column-skeleton-span-bottom-radius": radius(partial.bottom),
  };
}

/**
 * Writes the span inputs on `$column` (a body column painting its skeleton),
 * only the values that changed. A pure function of the column key and window,
 * so the day-column renderer writes it as its bind starts, before any load,
 * and the column's bind again.
 */
export function writeColumnSkeletonSpan($column: HTMLElement, input: ColumnSkeletonSpanInput): void {
  const properties = columnSkeletonSpanProperties(input);
  for (const name of SKELETON_SPAN_PROPERTY_NAMES) {
    if ($column.style.getPropertyValue(name) !== properties[name]) $column.style.setProperty(name, properties[name]);
  }
}

/**
 * The rule body for the skeleton layer. One element per column, painted with
 * one background colour and gradients: no per-hour or per-day elements. The
 * third paints the column background outside the span events can render in
 * (transparent on a column without one); the last four paint it in the wedges
 * outside the span's rounded corners (sized 0 at an edge that is not rounded).
 */
const TOP_RADIUS = "var(--column-skeleton-span-top-radius, 0px)";
const BOTTOM_RADIUS = "var(--column-skeleton-span-bottom-radius, 0px)";

/** One rounded-corner wedge: the outside colour beyond `radius` from the tile corner at `at`. */
function corner(at: string, radius: string): string {
  return `radial-gradient(
      circle at ${at},
      transparent calc(${radius} - 0.5px),
      var(--column-skeleton-outside-bg, transparent) calc(${radius} + 0.5px)
    )`;
}

export const SKELETON_LAYER_DECLARATIONS = `
  background-color: var(--column-skeleton-bg, var(--bg));
  background-image:
    repeating-linear-gradient(
      180deg,
      var(--std-calendar-column-layout-horizontal-color, var(--divider)) 0 1px,
      transparent 1px 100%
    ),
    linear-gradient(to right, var(--column-skeleton-day-divider, transparent) 0 1px, transparent 1px),
    linear-gradient(
      180deg,
      var(--column-skeleton-outside-bg, transparent) 0 var(--column-skeleton-span-top, 0px),
      transparent var(--column-skeleton-span-top, 0px) var(--column-skeleton-span-bottom, 100%),
      var(--column-skeleton-outside-bg, transparent) var(--column-skeleton-span-bottom, 100%)
    ),
    ${corner("100% 100%", TOP_RADIUS)},
    ${corner("0% 100%", TOP_RADIUS)},
    ${corner("100% 0%", BOTTOM_RADIUS)},
    ${corner("0% 0%", BOTTOM_RADIUS)};
  background-size:
    100% var(--std-calendar-column-layout-width, ${PIXELS_PER_HOUR}px),
    calc(100% / var(--column-skeleton-days, 1)) 100%,
    100% 100%,
    ${TOP_RADIUS} ${TOP_RADIUS},
    ${TOP_RADIUS} ${TOP_RADIUS},
    ${BOTTOM_RADIUS} ${BOTTOM_RADIUS},
    ${BOTTOM_RADIUS} ${BOTTOM_RADIUS};
  background-position:
    0 0,
    0 0,
    0 0,
    0 var(--column-skeleton-span-top, 0px),
    100% var(--column-skeleton-span-top, 0px),
    0 calc(var(--column-skeleton-span-bottom, 100%) - ${BOTTOM_RADIUS}),
    100% calc(var(--column-skeleton-span-bottom, 100%) - ${BOTTOM_RADIUS});
  background-repeat: repeat, repeat, repeat, no-repeat, no-repeat, no-repeat, no-repeat;
`;

/**
 * The attribute a body column carries when it paints its own skeleton. The
 * scroll host then keeps a `pending-layout` column visible, and the sheet
 * column hides only the stale content (it marks this column `content-stale`).
 */
export const PAINTS_SKELETON_ATTRIBUTE = "paints-skeleton";
